import React, { useRef, useEffect } from "react";
import debounce from "lodash/debounce";
import get from "lodash/get";
import SignaturePad from "signature_pad";
import cn from "clsx";
import { InputCustomChangeEventHandler } from "../../types";
import Button from "../Button";
//import { debug } from "../../utils/debug";
import "./InputSignature.css";

export interface InputSignatureProps {
  id: string;
  name: string;
  value?: string;
  labels?: {
    buttonClear: string;
  };
  width?: number;
  height?: number;
  className?: string;
  disabled?: boolean;
  onChange: InputCustomChangeEventHandler;
}

export const resizeCanvas = (
  canvas: HTMLCanvasElement | null,
  signaturePad: SignaturePad | null,
  handleChange: InputCustomChangeEventHandler,
  force?: boolean
): Function => {
  return debounce(() => {
    if (!signaturePad || !canvas || !canvas.offsetWidth) {
      return;
    }

    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    const newWidth = canvas.offsetWidth * ratio;
    const newHeight = canvas.offsetHeight * ratio;
    let changed = false;
    // update canvas size only if needed!
    // to avoid clearing the content
    if (newWidth !== canvas.width || force) {
      canvas.width = newWidth;
      changed = true;
    }
    if (newHeight !== canvas.height || force) {
      canvas.height = newHeight;
      changed = true;
    }

    const context = canvas.getContext("2d");
    if (context && changed) {
      context.scale(ratio, ratio);
    }

    if (handleChange && changed) {
      handleChange(null);
    }
  });
};

export default function InputSignature({
  id,
  name,
  value,
  labels,
  width,
  height,
  className,
  disabled,
  onChange
}: InputSignatureProps) {
  // Shared/internal context/state
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const padRef = useRef<SignaturePad | null>(null);

  const handleSignatureChange = (dataUrl: string | null) => {
    onChange(dataUrl, name);
  };

  const didDraw = () => {
    if (padRef.current) {
      const encoded = padRef.current.toDataURL("image/svg+xml");
      if (encoded && encoded.split) {
        const a = encoded.split(",")[1];

        // Decode base64 to raw
        if (a) {
          const svg = atob(a);
          handleSignatureChange(svg);
        }
      } else {
        handleSignatureChange(null);
      }
    }
  };

  const clearSignature = () => {
    if (padRef.current) {
      padRef.current.clear();
      handleSignatureChange(null);

      // new code
      /*const resizePad = resizeCanvas(
        canvasRef.current,
        padRef.current,
        onChange,
        true
      );
      resizePad();*/
      ////////
    }
  };

  const resizePad = resizeCanvas(canvasRef.current, padRef.current, onChange);

  useEffect(() => {
    if (canvasRef.current) {
      padRef.current = new SignaturePad(canvasRef.current, {
        onEnd: didDraw
      });

      window.addEventListener(
        "resize",
        resizePad as EventListenerOrEventListenerObject
      );

      // Ensure that it is resized so dimensions are all good
      // Sneaky trick ensures that it happens at some point in the JS event loop
      setTimeout(() => resizePad(), 0);

      return () => {
        window.removeEventListener(
          "resize",
          resizePad as EventListenerOrEventListenerObject
        );
      };
    }
  }, [canvasRef.current]);

  useEffect(() => {
    if (disabled) {
      if (padRef.current) {
        padRef.current.off();
      }
    } else {
      if (padRef.current) {
        padRef.current.on();
      }
    }

    // Ensure that it is resized after re-enabling so dimension are all good
    // Sneaky trick ensures that it happens at some point in the JS event loop
    setTimeout(() => resizePad(), 0);
  }, [disabled]);

  const canvasWidth = width ? width : 480;
  const canvasHeight = height ? height : 320;

  return (
    <div
      className={cn(
        "input-signature",
        className,
        disabled && "is-disabled",
        !!value && "has-value"
      )}
    >
      <div className="input-signature-pad">
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          style={{
            width: `${canvasWidth}px`,
            height: `${canvasHeight}px`
          }}
        />
        <input type="hidden" id={id} name={name} value={value || ""} />
      </div>
      <div className="input-signature-controls">
        <Button
          className="button-role-clear"
          type="button"
          theme="neutral"
          shape="default"
          label={get(labels, "buttonClear")}
          fullWidth
          onClick={clearSignature}
        />
      </div>
    </div>
  );
}
